import { Box, Button, Stack, Typography } from '@mui/material';
import { PlusIcon, SectionBlock } from '@schooly/style';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTeacherDashboardPermissions } from './useTeacherDashboardPermissions';
import { AssessmentsWidgetContainer } from './Widgets/Assessments/AssessmentsWidgetContainer';
import { GroupsSectionBlock } from './Widgets/Groups/GroupsSectionBlock';
import { MessagesWidget } from './Widgets/Messages/MessagesWidget';
import { ReportsWidget } from './Widgets/Reports/ReportsWidget';
import { TimetableSectionBlock } from './Widgets/Timetable/TimetableSectionBlock';

const SectionTitle: FC<{ id: string }> = ({ id }) => (
  <Typography variant="h2">
    <FormattedMessage id={id} />
  </Typography>
);

const columnSx = { '&:empty': { display: 'none' }, minWidth: 250, width: '100%' };

export const TeacherDashboardContent: FC = () => {
  const { canViewGroups, canViewAssessments, canViewReports, canViewTimetable, canViewMessages } =
    useTeacherDashboardPermissions();

  return (
    <Box sx={(theme) => ({ [theme.breakpoints.down('md')]: { overflowX: 'auto' } })}>
      <Stack
        gap={3}
        pt={2.5}
        alignItems="flex-start"
        sx={(theme) => ({
          flexDirection: canViewTimetable ? 'row' : 'column',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
        })}
      >
        <Stack flex={2} gap={2} minWidth={0} sx={columnSx}>
          {canViewGroups && <GroupsSectionBlock />}

          {canViewAssessments && (
            <SectionBlock title={<SectionTitle id="dashboard-teacher-MyAssessments" />}>
              <AssessmentsWidgetContainer />
            </SectionBlock>
          )}

          {canViewReports && (
            <SectionBlock title={<SectionTitle id="dashboard-teacher-MyReports" />}>
              <ReportsWidget />
            </SectionBlock>
          )}
        </Stack>

        <Stack
          flex={1}
          gap={2.5}
          sx={(theme) => ({
            ...columnSx,
            maxWidth: '50%',
            [theme.breakpoints.down('sm')]: { maxWidth: '100%' },
          })}
        >
          {canViewTimetable && <TimetableSectionBlock />}

          {canViewMessages && (
            <SectionBlock title={<SectionTitle id="dashboard-teacher-MyMessages" />}>
              <MessagesWidget />
            </SectionBlock>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export const EmptyState: FC<{
  titleId: string;
  subtitleId: string;
  buttonId?: string;
  onButtonClick?: VoidFunction;
}> = ({ titleId, subtitleId, buttonId, onButtonClick }) => (
  <Stack justifyContent="center" alignItems="center" sx={{ pt: 2.5, pb: 5, px: 2 }}>
    <Typography variant="h2" textAlign="center">
      <FormattedMessage id={titleId} />
    </Typography>
    <Typography variant="h3" textAlign="center" mt={1} color="text.primary">
      <FormattedMessage id={subtitleId} />
    </Typography>
    {buttonId && (
      <Button startIcon={<PlusIcon />} sx={{ mt: 3 }} onClick={onButtonClick}>
        <FormattedMessage id={buttonId} />
      </Button>
    )}
  </Stack>
);
